// Odesílání událostí o přístupech k částem aplikace (etapa K1).
// Viz doc/plans/usage-analytics-client.md sekce 2.2.
//
// Záměrně NEpoužívá `api` z client.js — ten čeká na odpověď a hází chyby. Tady
// nechceme ani jedno: sledování přístupů nesmí nikdy zhoršit chod aplikace, takže
// se každé selhání tiše ignoruje a nikdy se neopakuje (žádný retry).
//
// Dávkování: událost se zařadí do fronty a odešle se, když nastane kterákoli z podmínek:
//   - ve frontě je MAX_BATCH událostí,
//   - uplynulo FLUSH_DELAY_MS od první události ve frontě,
//   - dokument přechází do 'hidden' (visibilitychange) → poslední dávka přes sendBeacon.

const BASE_URL = import.meta.env.VITE_API_URL || '/api'

// Vypínač pro vývoj, aby lokální klikání nešpinilo data. Vypíná se jen explicitním
// 'false' — server má vlastní nezávislý přepínač (USAGE_TRACKING_ENABLED).
const ENABLED = import.meta.env.VITE_USAGE_TRACKING !== 'false'

// Server bere max. 20 událostí v jedné dávce (delší ořízne), držíme se pod tím.
const MAX_BATCH = 10
const FLUSH_DELAY_MS = 3000

const SESSION_KEY_STORAGE = 'cw_usage_session'
const SESSION_KEY_LENGTH = 32
const TOKEN_KEY = 'cw_auth_token'

const dbg = import.meta.env.DEV ? console.debug.bind(console) : () => {}

// ---------------------------------------------------------------------------
// session_key — náhodný identifikátor relace, zaniká zavřením karty
// ---------------------------------------------------------------------------

const SESSION_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789'

function randomKey(length) {
  const bytes = new Uint8Array(length)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, b => SESSION_ALPHABET[b % SESSION_ALPHABET.length]).join('')
}

let cachedSessionKey = null

// sessionStorage může v prohlížeči chybět nebo házet (privátní režim, zakázaná
// úložiště) — klíč pak žije jen v paměti modulu, což pro účel "kolik různých relací"
// stačí a rozhodně nesmí shodit volajícího.
export function getSessionKey() {
  if (cachedSessionKey) return cachedSessionKey
  try {
    const stored = sessionStorage.getItem(SESSION_KEY_STORAGE)
    if (stored) {
      cachedSessionKey = stored
      return cachedSessionKey
    }
  } catch { /* úložiště nedostupné — klíč zůstane jen v paměti */ }

  cachedSessionKey = randomKey(SESSION_KEY_LENGTH)
  try {
    sessionStorage.setItem(SESSION_KEY_STORAGE, cachedSessionKey)
  } catch { /* viz výše */ }
  return cachedSessionKey
}

// ---------------------------------------------------------------------------
// Fronta
// ---------------------------------------------------------------------------

let queue = []
let flushTimer = null

function clearFlushTimer() {
  if (flushTimer !== null) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
}

// Odeslání dávky. `useBeacon` = zavírá se karta, fetch už nemusí doběhnout.
//
// Primární cesta je `fetch` s `keepalive` a hlavičkou Authorization, aby se
// přihlášený uživatel neztratil (plán sekce 2.2, rozhodnutí k otázce 1).
// `sendBeacon` neumí posílat hlavičky, takže dávka odeslaná při zavírání karty
// spadne na serveru pod anonyma — token se do těla nedává.
function send(events, useBeacon) {
  const payload = JSON.stringify({ session_key: getSessionKey(), events })
  const url = `${BASE_URL}/usage`

  if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'application/json' })
    if (navigator.sendBeacon(url, blob)) {
      dbg('[usage] odesláno přes sendBeacon:', events.length)
      return
    }
    // sendBeacon vrátil false (fronta prohlížeče plná) → zkusit fetch keepalive níže.
  }

  const token = localStorage.getItem(TOKEN_KEY)
  fetch(url, {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: payload,
  })
    .then(() => dbg('[usage] odesláno přes fetch keepalive:', events.length))
    .catch(() => { /* tiše ignorovat — žádná hláška, žádný retry */ })
}

// Odešle vše, co je ve frontě. Volá se z časovače, po naplnění dávky a při
// visibilitychange; jde volat i ručně.
export function trackFlush(useBeacon = false) {
  clearFlushTimer()
  if (!queue.length) return
  const events = queue
  queue = []
  send(events, useBeacon)
}

// Zařadí jednu událost do fronty.
//
//   track({ context, category, product_slug, gh_id, subject_type, subject_id })
//
// Povinné je `context` a `product_slug`; zbytek je volitelný. Nedefinované klíče
// se do těla nedávají, ať se posílá jen to, co volající opravdu ví.
export function track(event) {
  if (!ENABLED) return
  if (!event || !event.context || !event.product_slug) return

  const payload = { context: event.context, product_slug: event.product_slug }
  if (event.category)     payload.category     = event.category
  if (event.gh_id != null && event.gh_id !== '') payload.gh_id = event.gh_id
  if (event.subject_type && event.subject_id != null && event.subject_id !== '') {
    payload.subject_type = event.subject_type
    payload.subject_id   = String(event.subject_id)
  }

  queue.push(payload)
  dbg('[usage] zařazeno:', payload, `(fronta: ${queue.length})`)

  if (queue.length >= MAX_BATCH) {
    trackFlush()
  } else if (flushTimer === null) {
    // Časovač se počítá od PRVNÍ události ve frontě — proto se nerestartuje, když
    // do neprázdné fronty přibude další.
    flushTimer = setTimeout(() => trackFlush(), FLUSH_DELAY_MS)
  }
}

// Přechod karty na pozadí (i zavření — 'hidden' je poslední spolehlivá událost,
// 'unload' se v moderních prohlížečích nemusí spustit).
if (ENABLED && typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') trackFlush(true)
  })
}
